import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Download, Package, Loader2, FileText } from 'lucide-react';

/**
 * Mira Founders Kit · CEO download tile.
 *
 * Lets the CEO export the entire Mira Founders Kit as a ZIP with one click.
 * The kit is a portable blueprint (system prompt · intent catalog · WA
 * routing doctrine · Soul Engine schema · executor skeleton · integration
 * playbooks · production incidents · seed JSONs) for porting Mira to
 * another Emergent project.
 *
 * Backend endpoints:
 *   GET /api/admin/mira-kit/manifest  → JSON file list + sizes
 *   GET /api/admin/mira-kit/download  → application/zip
 *
 * Both are CEO-only (verified by `auth.get_current_user` + role check).
 */
export default function MiraFoundersKitTile() {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/admin/mira-kit/manifest')
      .then((r) => setManifest(r.data))
      .catch(() => setManifest({ ok: false, error: 'unavailable' }))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get('/admin/mira-kit/download', { responseType: 'blob' });
      // Build a blob URL and click an anchor to trigger the browser download.
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mira_Founders_Kit_v1_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Mira Founders Kit downloaded.');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error('CEO-only download. Please log in as CEO.');
      } else {
        toast.error(`Download failed: ${e?.message || 'unknown error'}`);
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border p-4 flex items-center gap-2" style={{ borderColor: '#E5E1DC', background: '#FAF8F5' }}>
        <Loader2 size={14} className="animate-spin" style={{ color: '#2A7B88' }} />
        <span className="text-xs" style={{ color: '#6B7280' }}>Loading Mira Kit manifest…</span>
      </div>
    );
  }

  if (!manifest?.ok) {
    return (
      <div className="rounded-xl border p-4" style={{ borderColor: '#E5E1DC', background: '#FAF8F5' }} data-testid="mira-kit-tile-error">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Mira Founders Kit</p>
        <p className="text-[11px] mt-1" style={{ color: '#B91C1C' }}>
          Kit unavailable. {manifest?.error || ''} (Redeploy the backend if this persists.)
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: '#E5E1DC', background: 'linear-gradient(135deg, #FAF8F5 0%, #F0EBE3 100%)' }}
      data-testid="mira-founders-kit-tile"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1B2A4A' }}>
            <Package size={18} style={{ color: '#F5C86B' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Mira Founders Kit</p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold" style={{ backgroundColor: '#1B2A4A', color: '#F5C86B' }}>v{manifest.version}</span>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Snapshot · {manifest.snapshot_date} · {manifest.file_count} files · {manifest.total_kb} KB
            </p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
              Portable blueprint: system prompt · intent catalog · WA routing · Soul Engine schema · seed JSONs.
            </p>
          </div>
        </div>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white transition disabled:opacity-60"
          style={{ backgroundColor: '#2A7B88' }}
          data-testid="mira-founders-kit-download-btn"
        >
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {downloading ? 'Preparing…' : 'Download ZIP'}
        </button>
      </div>

      <details className="mt-3 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
        <summary className="text-[11px] cursor-pointer flex items-center gap-1" style={{ color: '#1B2A4A', fontWeight: 600 }}>
          <FileText size={11} /> What's inside (click to view {manifest.file_count} files)
        </summary>
        <ul className="mt-2 space-y-1" data-testid="mira-founders-kit-file-list">
          {manifest.files.map((f) => (
            <li key={f.path} className="text-[10.5px] flex items-center justify-between gap-3 font-mono" style={{ color: '#6B7280' }}>
              <span style={{ color: '#1B2A4A' }}>{f.path}</span>
              <span>{(f.size_bytes / 1024).toFixed(1)} KB</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
